import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import ChartHeader from "../ChartHeader";
import { images } from "../../common/images";
import moment from "moment";

export default function VoltageLineChart({
  selectedDeviceSelectedDayData,
  deviceId,
}) {
  const [series, setSeries] = useState([{ name: "Voltage", data: [] }]);
  const [options, setOptions] = useState({
    chart: {
      type: "area",
      stacked: false,
      height: "215px",
      zoom: {
        type: "x",
        enabled: true,
        autoScaleYaxis: true,
      },
      toolbar: {
        autoSelected: "zoom",
        export: {
          csv: {
            categoryFormatter(x) {
              return new Date(x)
                .toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })
                .replace(",", "");
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
    },
    title: {
      text: "",
      align: "left",
    },
    yaxis: {
      labels: {
        formatter: (val) => val.toFixed(0),
      },
      title: {
        text: "Voltage",
      },
    },
    xaxis: {
      ...(selectedDeviceSelectedDayData.length > 1 ? {
        categories: selectedDeviceSelectedDayData.map((item) => new Date(item.date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).replace(',', '')),
      } : {
        type: "datetime",
        labels: {
          formatter: (val) =>
            new Date(val).toLocaleTimeString("en-US", {
              ...(selectedDeviceSelectedDayData.length > 1 ? {
                day: "2-digit",
                month: "short"
              } : {
                hour: "numeric",
                minute: "numeric"
              }),
            }),
        },
      })

    },
    tooltip: {
      shared: false,
      x: {
        formatter: (val, opts) => {
          // 🔒 guard checks (IMPORTANT)
          const categories = opts?.w?.globals?.categoryLabels;

          // when categories exist (length > 1 case)
          if (
            selectedDeviceSelectedDayData.length > 1 &&
            Array.isArray(categories) &&
            categories[opts.dataPointIndex] !== undefined
          ) {
            return categories[opts.dataPointIndex];
          }

          // fallback → datetime axis
          if (!isNaN(val)) {
            return new Date(val).toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "numeric",
            });
          }

          // final fallback
          return val;
        },
      },
      y: {
        formatter: (val) => val.toFixed(2),
      },
    },
  });

  useEffect(() => {
    if (
      selectedDeviceSelectedDayData &&
      selectedDeviceSelectedDayData.length === 1
    ) {
      let sortedLoadLogs = [];
      for (let data of selectedDeviceSelectedDayData) {
        if (Object.keys(data).length === 0) continue;
        sortedLoadLogs = [
          ...sortedLoadLogs,
          ...(data?.loadLogs
            ? data.loadLogs.filter((item) => item.ts !== undefined).sort((a, b) => new Date(a.ts) - new Date(b.ts))
            : [])
        ];
      }

      let sortAccordingM = {
        0: [],
      };
      for (let p of sortedLoadLogs) {
        if (p.ts === undefined) continue
        if (p.m === undefined || p.m === 0) {
          sortAccordingM["0"].push(p);
        } else {
          if (sortAccordingM[`${p.m}`] === undefined) {
            sortAccordingM[`${p.m}`] = [];
          }
          sortAccordingM[`${p.m}`].push(p);
        }
      }

      let data = [];

      for (let i of Object.keys(sortAccordingM)) {
        let power = [];
        for (let j of sortAccordingM[i]) {
          let data = [j.ts, j.v];
          power.push(data);
        }

        data.push({
          data: power,
          name: `${deviceId}-Phase ${Number(i) + 1}`,
        });
      }

      setSeries(data);
    } else if (selectedDeviceSelectedDayData && selectedDeviceSelectedDayData.length > 1) {
      let sortedLoadLogs = [];
      for (let data of selectedDeviceSelectedDayData) {
        if (Object.keys(data).length === 0) continue;
        let logs = (data?.loadLogs !== undefined ? data?.loadLogs?.filter((item) => item.ts !== undefined).sort(
          (a, b) => parseFloat(a.ts) - parseFloat(b.ts)
        ) : [])

        let sortAccordingM = {
          0: [],
        };
        for (let p of logs) {
          if (p.ts === undefined) continue
          if (p.m === undefined || p.m === 0) {
            sortAccordingM["0"].push(p);
          } else {
            if (sortAccordingM[`${p.m}`] === undefined) {
              sortAccordingM[`${p.m}`] = [];
            }
            sortAccordingM[`${p.m}`].push(p);
          }
        }


        let totalPhaseAvg = 0
        for (let i of Object.keys(sortAccordingM)) {
          let phaseAvg = sortAccordingM[i]?.reduce((a, b) => a + b.v, 0) / sortAccordingM[i]?.length
          if (!isNaN(phaseAvg)) {
            totalPhaseAvg += phaseAvg
          }
        }
        let avg = totalPhaseAvg / Object.keys(sortAccordingM).length
        avg = isNaN(avg) ? 0 : Number(avg.toFixed(2))
        sortedLoadLogs.push(avg)
      }
      setSeries([{ data: sortedLoadLogs, name: "Voltage" }])

    } else {
      setSeries([]); // Clear the series when data is empty
    }
    setOptions({
      chart: {
        type: "area",
        stacked: false,
        height: "215px",
        zoom: {
          type: "x",
          enabled: true,
          autoScaleYaxis: true,
        },
        toolbar: {
          autoSelected: "zoom",
          export: {
            csv: {
              categoryFormatter(x) {
                return new Date(x)
                  .toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                  .replace(",", "");
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0,
      },
      title: {
        text: "",
        align: "left",
      },
      yaxis: {
        labels: {
          formatter: (val) => val.toFixed(0),
        },
        title: {
          text: "Voltage",
        },
      },
      xaxis: {
        ...(selectedDeviceSelectedDayData.length > 1 ? {
          categories: selectedDeviceSelectedDayData.map((item) => new Date(item.date).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).replace(',', '')),
        } : {
          type: "datetime",
          labels: {
            formatter: (val) =>
              new Date(val).toLocaleTimeString("en-US", {
                ...(selectedDeviceSelectedDayData.length > 1 ? {
                  day: "2-digit",
                  month: "short"
                } : {
                  hour: "numeric",
                  minute: "numeric"
                }),
              }),
          },
        })

      },
      tooltip: {
        shared: false,
        x: {
          formatter: (val, opts) => {
            // 🔒 guard checks (IMPORTANT)
            const categories = opts?.w?.globals?.categoryLabels;

            // when categories exist (length > 1 case)
            if (
              selectedDeviceSelectedDayData.length > 1 &&
              Array.isArray(categories) &&
              categories[opts.dataPointIndex] !== undefined
            ) {
              return categories[opts.dataPointIndex];
            }

            // fallback → datetime axis
            if (!isNaN(val)) {
              return new Date(val).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "numeric",
              });
            }

            // final fallback
            return val;
          },
        },
        y: {
          formatter: (val) => val.toFixed(2),
        },
      },
    })
  }, [selectedDeviceSelectedDayData]);
 
  return (
    <div>
      <ChartHeader
        name="Voltage"
        // subName={`${selectedDeviceSelectedDayData?.[0]?.loadLogs?.slice(-1)?.[0]?.v.toFixed(2) || 0} V`}
        image={images.temperature}
        textColor="#322FC8"
        iconBackgroundColor="rgba(50, 47, 200, 0.10)"
      />
      {
        series.length === 0 ? null : <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={215}
        />
      }
    </div>
  );
}
